import {
    AllowedInvestmentAccountTypes,
    DrivewealthAccountManagementType,
    DrivewealthCreateAccountError,
    DrivewealthCreateCustomerRequest,
    DrivewealthDripDisclosures,
    DrivewealthEnrollment,
    DrivewealthEnrollmentDraft,
    DrivewealthEnrollmentDto,
    DrivewealthWizardState,
    DrivewealthWizardStep,
    FetchState,
    FetchStateKey,
    UserRtqEligibilityResponse,
} from "@/shared/types";
import { errorIsStatus } from "@/shared/errorHelper";
import * as storeHelper from "@/shared/storeHelper";
import * as drivewealthService from "@/shared/api/drivewealthService";
import _ from "lodash";
import * as drivewealth from "@/shared/custodianSettings/drivewealthTypes";
import { Location } from "vue-router";
import Vue from "vue";
import { defineStore } from "pinia";
import { useNotifierStore } from "@/shared/store/notifier";
import { useDrivewealthStore } from "@/shared/store/drivewealth";
import { useRiskToleranceQuestionnaireStore } from "@/shared/store/riskToleranceQuestionnaire";
import { useDrivewealthSweepStore } from "@/shared/store/drivewealthSweep";
import { useFeatureFlagStore } from "@/shared/store/featureFlag";
import { useInvestmentDailyBalanceStore } from "@/shared/store/investmentDailyBalance";
import { useHsabInvestmentStore } from "@/shared/store/hsabInvestment";
import { useWizardStore } from "@/employee/store/wizard";
import { useSigningDocumentStore } from "@/admin/store/signingDocument";
import { useCustodianSettingsStore } from "@/shared/store/custodianSettings";
import { templatedFetchKeyStore } from "@/shared/store/fetchKeyedPlugin";
import { fetchStateKeyMatch } from "@/shared/fetchStateHelper";

function emptyEnrollment (): DrivewealthEnrollment {
    return {
        isPreenrolled: false,
        employmentStatus: null,
        employmentType: null,
        occupation: null,
        isBroker: false,
        directorCompany: null,
        politicallyExposedNames: null,
        investmentObjective: null,
        investmentExperience: null,
        annualIncome: 0,
        netWorthTotal: 0,
        riskTolerance: null,
        accountType: null,
        citizenship: null,
        signatureContent: null,
        draft: {
            isDirty: false,
            citizenship: "USA",
            employmentStatus: null,
            employmentType: null,
            occupation: null,
            isBroker: null,
            isDirector: null,
            directedCompany: null,
            isPoliticallyExposed: null,
            politicallyExposedNames: null,
            annualIncome: 0,
            netWorthTotal: 0,
            didAgreeToTerms: false,
            signatureContent: null,
        },
        investmentAccountType: DrivewealthAccountManagementType.None,
        managedRtqScore: null
    };
}

export const initialState: DrivewealthWizardState = {
    accountType: null,
    steps: [],
    completedSteps: [],
    createAccountFetchState: FetchState.Uninitialized,
    createAccountError: null,
    routerLocation: null,
    investmentStrategy: {
        portfolioId: null
    },
    enrollment: {
        fetchKey: null,
        fetchState: FetchState.Uninitialized,
        ...emptyEnrollment(),
    }
};

const useEnrollmentTypeSubStore = defineStore("drivewealthWizard_enrollmentType", () => {
    const emptyResourceGenerator = () => ({
        hasAdvisory: false,
        hasRiaManaged: false,
        hasSelfDirected: false,
        allowPreEnrollment: false,
        isCuratedRtqEnabled: false
    });
    const retryOptions = {
        maxAttempts: 5
    };
    return templatedFetchKeyStore<AllowedInvestmentAccountTypes, any>(
        emptyResourceGenerator,
        null,
        drivewealthService.getEmployeeInvestmentTypes,
        retryOptions
    );
});

function emptyDripDisclosures (): DrivewealthDripDisclosures {
    return {
        dripDisclosureAcknowledgmentRequired: false,
        equitiesDripDisclosure: {
            disclosureId: null,
            documentName: null,
            documentUrl: null
        },
        mutualFundsDripDisclosure: {
            disclosureId: null,
            documentName: null,
            documentUrl: null
        }
    };
}

const useDripDisclosuresSubStore = defineStore("drivewealthWizard_dripDisclosure", () => {
    const emptyResourceGenerator = emptyDripDisclosures;
    const retryOptions = {
        maxAttempts: 5
    };
    return templatedFetchKeyStore<DrivewealthDripDisclosures, any>(
        emptyResourceGenerator,
        null,
        drivewealthService.getDividendReinvestmentDisclosures,
        retryOptions
    );
});

interface WizardIntiationRites {
    accountType: DrivewealthAccountManagementType;
    hasAnAccount: boolean;
    isUserOnboarding: boolean;
    featureFlags: any;
    isAccountClaimsWorkflow: boolean;
    userRtqEligibilityResponse: UserRtqEligibilityResponse;
    isHsabCustodian?: boolean;
}

const mapToDrivewealthAccountManagementType =
    (accountManageMentType: drivewealth.AccountType): DrivewealthAccountManagementType => {
    switch (accountManageMentType) {
        case "ADVISORY":
            return DrivewealthAccountManagementType.Advisory;
        case "SELF":
            return DrivewealthAccountManagementType.OpenBrokerage;
        case "RIA_MANAGED":
            return DrivewealthAccountManagementType.RiaManaged;
    }
};

const mapDrivewealthAccountManagementTypeToDisplayCopy =
    (accountManageMentType: DrivewealthAccountManagementType) => {
    switch (accountManageMentType) {
        case DrivewealthAccountManagementType.Advisory:
            return "Select";
        case DrivewealthAccountManagementType.OpenBrokerage:
            return "Choice";
        case DrivewealthAccountManagementType.RiaManaged:
            return "Managed";
    }
};

const initializeWizardData = (data: WizardIntiationRites) => {
    const wizardSteps: DrivewealthWizardStep[] = [];
    const enableCuratedRtqViewForWizard = (
        data.accountType === DrivewealthAccountManagementType.Advisory &&
        data.userRtqEligibilityResponse && data.userRtqEligibilityResponse.curatedRtqEnabledByEmployerOrPartner);

    const enableHsabDrivewealthManagedView = (
        data.accountType === DrivewealthAccountManagementType.RiaManaged &&
        data.isHsabCustodian);

    if (data.hasAnAccount && data.isAccountClaimsWorkflow) {
        wizardSteps.push(DrivewealthWizardStep.RequiredDisclosures);
    }
    wizardSteps.push(DrivewealthWizardStep.SelectAccountType);
    wizardSteps.push(DrivewealthWizardStep.RequiredDisclosures);


    if (data.accountType === DrivewealthAccountManagementType.RiaManaged && !enableHsabDrivewealthManagedView) {
        wizardSteps.push(DrivewealthWizardStep.PortfolioSelection);
        wizardSteps.push(DrivewealthWizardStep.PortfolioConfirmation);
    }

    wizardSteps.push(DrivewealthWizardStep.TermsAndConditions);
    wizardSteps.push(DrivewealthWizardStep.CustomerSignature);

    if (enableCuratedRtqViewForWizard) {
        wizardSteps.push(DrivewealthWizardStep.RiskToleranceQuestionnaire);
    } else if (enableHsabDrivewealthManagedView) {
        wizardSteps.push(DrivewealthWizardStep.PortfolioSelection);
        wizardSteps.push(DrivewealthWizardStep.PortfolioConfirmation);
    }

    wizardSteps.push(DrivewealthWizardStep.AutomatedFunding);

    return {
        wizardSteps,
        accountType: data.accountType
    };
};

type WizardIntializationData = ReturnType<typeof initializeWizardData>;

export const useDrivewealthWizardStore = defineStore("drivewealthWizard", {
    state: (): DrivewealthWizardState => (_.cloneDeep(initialState)),
    getters: {
        enrollmentType: () => useEnrollmentTypeSubStore(),
        dividendReinvestmentDisclosure: () => useDripDisclosuresSubStore(),
        drivewealthAccountManagementType: state => state.accountType,
        dividendReinvestmentDisclosures: () => useDripDisclosuresSubStore(),
        firstIncompleteStep: state => {
            if (state.steps.length === 0) {
                return null;
            }
            const firstIncompleteStep = state.steps.find(s => !state.completedSteps.find(cs => cs === s));
            return firstIncompleteStep || DrivewealthWizardStep.Completed;
        },
        firstStep: state => {
            if (state.steps.length === 0) {
                return null;
            }
            return state.steps[0];
        },
        getNextWizardStep: state => currentStep => {
            if (state.steps.length === 0) return null;

            const index = state.steps.findIndex(s => s === currentStep);
            if (index + 1 >= state.steps.length) return null;

            return state.steps[index + 1];
        },
        getPreviousWizardStep: state => currentStep => {
            if (state.steps.length === 0) return null;

            const index = state.steps.findIndex(s => s === currentStep);
            if (index === 0) return null;

            return state.steps[index - 1];
        },
        isFinalWizardStep: state => (step: DrivewealthWizardStep) => {
            if (state.steps.length === 0) return false;

            return state.steps[state.steps.length - 1] === step;
        },
        isStepValid: state => currentStep => {
            return !!state.steps.find(s => s === currentStep);
        },
        wizardStepNumber: state => (step: DrivewealthWizardStep) => state.steps.findIndex(s => s === step) + 1,
        numberOfWizardSteps: state => state.steps.length,
        hasAnAccount () {
            const drivewealthStore = useDrivewealthStore();
            return drivewealthStore.hasAnAccount;
        },
        draftIsDirty: state => state.enrollment.draft.isDirty,
        createAccountLoading: state => state.createAccountFetchState === FetchState.Loading,
        disclosureEmploymentStatus: state => state.enrollment.draft.employmentStatus,
        disclosureEmploymentType: state => state.enrollment.draft.employmentType,
        disclosureOccupation: state => state.enrollment.draft.occupation,
        disclosureCitizenship: state => state.enrollment.draft.citizenship,
        disclosureIsBroker: state => state.enrollment.draft.isBroker,
        disclosureIsDirector: state => state.enrollment.draft.isDirector,
        disclosureDirectedCompany: state => state.enrollment.draft.directedCompany,
        disclosureIsPoliticallyExposed: state => state.enrollment.draft.isPoliticallyExposed,
        disclosurePoliticallyExposedNames: state => state.enrollment.draft.politicallyExposedNames,
        disclosureAnnualIncome: state => state.enrollment.draft.annualIncome,
        disclosureNetWorthTotal: state => state.enrollment.draft.netWorthTotal,
        termsDidAgree: state => state.enrollment.draft.didAgreeToTerms,
        signatureContent: state => state.enrollment.draft.signatureContent,
        investmentStrategyPortfolioId: state => state.investmentStrategy.portfolioId,
        hasSignatureContent () {
            if (this.enrollment.signatureContent) {
              return true;
            }
            return false;
        },
        createCustomerRequest: (state): DrivewealthCreateCustomerRequest => ({
            accountType: state.accountType,
            portfolioId: state.accountType === DrivewealthAccountManagementType.RiaManaged ? state.investmentStrategy.portfolioId : null,

            employmentStatus: state.enrollment.draft.employmentStatus,
            employmentType: state.enrollment.draft.employmentType,
            citizenship: state.enrollment.draft.citizenship,
            occupation: state.enrollment.draft.occupation,
            isBroker: state.enrollment.draft.isBroker || false,
            annualIncome: Math.ceil(state.enrollment.draft.annualIncome),
            netWorthTotal: Math.ceil(state.enrollment.draft.netWorthTotal),
            signatureContent: state.enrollment.draft.signatureContent,
            directorCompany: state.enrollment.draft.isDirector
                ? state.enrollment.draft.directedCompany
                : null,

            politicallyExposedNames: state.enrollment.draft.isPoliticallyExposed
                ? state.enrollment.draft.politicallyExposedNames
                : null,

            investmentObjective: "LONG_TERM",
            investmentExperience: "NONE",
            riskTolerance: "LOW",
            equitiesDripDisclosureId: useDripDisclosuresSubStore().dripDisclosureAcknowledgmentRequired ?
                useDripDisclosuresSubStore().equitiesDripDisclosure.disclosureId : null,
            mutualFundsDripDisclosureId: useDripDisclosuresSubStore().dripDisclosureAcknowledgmentRequired ?
                useDripDisclosuresSubStore().mutualFundsDripDisclosure.disclosureId : null,
            investmentAccountType: state.accountType,
            managedRtqScore: state.enrollment.managedRtqScore
        }),

        enrollmentTypeLoaded: () => useEnrollmentTypeSubStore().fetchState === FetchState.Loaded,
        isAllowedToPreenroll () {
            return this.enrollmentType.allowPreEnrollment;
        },
        enrollmentAccountType: state => state.enrollment.investmentAccountType,
        isEnrollmentLoaded: state => state.enrollment.fetchState === FetchState.Loaded,
        isPreenrolled: state => state.enrollment.isPreenrolled,
        isBalanceAndIsAllowedToPreenrollLoaded () {
            const investmentDailyBalanceStore = useInvestmentDailyBalanceStore();
            return this.enrollmentTypeLoaded &&
                investmentDailyBalanceStore.isHsaBalanceAndThresholdLoaded;
        },
        isBelowOpenAccountThreshold () {
            const investmentDailyBalanceStore = useInvestmentDailyBalanceStore();
            return investmentDailyBalanceStore.isHsaBalanceAndThresholdLoaded &&
                !investmentDailyBalanceStore.hasEnoughToOpenAccount;
        },
        isUserOnboarding () {
            const wizardStore = useWizardStore();
            return wizardStore.wizardState === FetchState.Loaded &&
                !wizardStore.isComplete;
        },
        isPreenrolling () {
            return this.enrollmentTypeLoaded &&
                this.isAllowedToPreenroll &&
                (this.isBelowOpenAccountThreshold || this.isUserOnboarding);
        },
        isCreatingAccount () {
            return !this.isBelowOpenAccountThreshold;
        }
    },
    actions: {
        setRouterLocation (location: Location) {
            Vue.set(this, "routerLocation", location);
        },
        initializeWizardSteps (data: WizardIntializationData) {
            this.accountType = data.accountType;
            this.steps = data.wizardSteps;
        },
        setWizardStepComplete (step: DrivewealthWizardStep) {
            this.completedSteps.push(step);
        },
        setToDrivewealthEnrollmentDraft ({ key, value }: {
            key: keyof DrivewealthEnrollmentDraft,
            value: any
        }) {
            _.set(this.enrollment.draft, key, value);
            this.enrollment.draft.isDirty = true;
        },
        setTermsDidAgree (didAgree: boolean) {
            this.enrollment.draft.didAgreeToTerms = didAgree;
        },
        setSignatureContent (signatureContent: string) {
            this.enrollment.draft.signatureContent = signatureContent;
        },
        setPortfolioId (portfolioId: string) {
            this.investmentStrategy.portfolioId = portfolioId;
        },
        setCreateAccountFetchState (fetchState: FetchState) {
            this.createAccountFetchState = fetchState;
        },
        setWizardStepIncomplete (step: DrivewealthWizardStep) {
            this.completedSteps = this.completedSteps.filter(v => v !== step);
        },
        clearCompletedSteps () {
            this.completedSteps = [];
        },
        setPreenrollmentUnitialized () {
            this.enrollment.fetchKey = null;
            this.enrollment.fetchState = FetchState.Uninitialized;
        },
        resetPreenrollment () {
            this.enrollment.isPreenrolled = false;
        },
        setPreenrollmentFetchState (fetchState: FetchState) {
            this.enrollment.fetchState = fetchState;
        },
        forceStepIncomplete (step: DrivewealthWizardStep) {
            this.setWizardStepIncomplete(step);
            return Promise.resolve();
        },
        selectAccountType (accountType: DrivewealthAccountManagementType, isAccountClaimsWorkflow?: boolean) {
            this.clearCompletedSteps();
            const drivewealthStore = useDrivewealthStore();
            const riskToleranceQuestionnaireStore = useRiskToleranceQuestionnaireStore();
            const featureFlagStore = useFeatureFlagStore();
            const custodianSettingsStore = useCustodianSettingsStore();
            return Promise.all([riskToleranceQuestionnaireStore.getUserRtqEligibilityResponse()])
            .then(_ => {
                this.initializeWizardSteps(initializeWizardData({
                    accountType,
                    hasAnAccount: drivewealthStore.hasAnAccount,
                    isUserOnboarding: this.isUserOnboarding,
                    featureFlags: featureFlagStore.flags,
                    isAccountClaimsWorkflow,
                    userRtqEligibilityResponse: riskToleranceQuestionnaireStore.userRtqEligibilityResponse,
                    isHsabCustodian: custodianSettingsStore.isHsaBankCustodian
                }));
                this.setWizardStepComplete(DrivewealthWizardStep.AccountTypeChange);
                this.setWizardStepComplete(DrivewealthWizardStep.SelectAccountType);
            });
        },
        startWizard (routerLocation: Location, isAccountClaimsWorkflow?: boolean, accountId?: number) {
            this.setRouterLocation(routerLocation);
            const drivewealthStore = useDrivewealthStore();
            const featureFlagStore = useFeatureFlagStore();
            const riskToleranceQuestionnaireStore = useRiskToleranceQuestionnaireStore();

            let accountType = this.accountType || "advisory";

            if (accountId) {
                const accountSummary = drivewealthStore.accountSummaryById(accountId);
                const mappedAccountType = accountSummary.accountManagementType;
                accountType = mappedAccountType || accountType;
            }
            return Promise.all([riskToleranceQuestionnaireStore.getUserRtqEligibilityResponse()])
            .then(_ => {
                this.initializeWizardSteps(initializeWizardData({
                    accountType,
                    hasAnAccount: drivewealthStore.hasAnAccount,
                    isUserOnboarding: this.isUserOnboarding,
                    featureFlags: featureFlagStore.flags,
                    isAccountClaimsWorkflow,
                    userRtqEligibilityResponse: riskToleranceQuestionnaireStore.userRtqEligibilityResponse
                }));
            });
        },
        startWizardStepComplete (step: DrivewealthWizardStep): Promise<any> {
            this.setWizardStepComplete(step);
            return Promise.resolve();
        },
        setInvestmentStrategyPortfolioId (portfolioId: string): Promise<void> {
            this.setPortfolioId(portfolioId);
            return Promise.resolve();
        },
        setEnrollmentLoading (fetchKey: FetchStateKey) {
            this.enrollment.fetchState = FetchState.Loading;
            this.enrollment.fetchKey = fetchKey;
        },
        setEnrollmentValueLoaded ({ data, fetchKey }: {
            data: DrivewealthEnrollmentDto,
            fetchKey: Partial<FetchStateKey>
        }) {
            if (!fetchStateKeyMatch(this.enrollment.fetchKey, fetchKey)) {
                return; // request is stale
            }

            this.enrollment.fetchState = FetchState.Loaded;

            this.enrollment = { ...this.enrollment, ...data };

            if ((this.enrollment.draft && this.enrollment.draft.isDirty) || !data.accountType) {
                return;
            }
            this.enrollment.draft.annualIncome = data.annualIncome;
            this.enrollment.draft.netWorthTotal = data.netWorthTotal;
            this.enrollment.draft.citizenship = data.citizenship;
            this.enrollment.draft.isDirector = !!data.directorCompany;
            this.enrollment.draft.directedCompany = data.directorCompany;
            this.enrollment.draft.employmentStatus = data.employmentStatus;
            this.enrollment.draft.employmentType = data.employmentType;
            this.enrollment.draft.occupation = data.occupation;
            this.enrollment.draft.isBroker = data.isBroker;
            this.enrollment.draft.isPoliticallyExposed = !!data.politicallyExposedNames;
            this.enrollment.draft.politicallyExposedNames = data.politicallyExposedNames;
            this.enrollment.managedRtqScore = data.managedRtqScore;
        },
        invalidateEnrollmentLoadedState (newFetchKey: FetchStateKey): void {
            if (fetchStateKeyMatch(this.enrollment.fetchKey, newFetchKey)) {
                return;
            }
            this.enrollment = {
                ...emptyEnrollment(),
                fetchKey: null,
                fetchState: FetchState.Uninitialized,
            };
        },
        preenrollCustomer (): Promise<any> {
            const request: DrivewealthCreateCustomerRequest = this.createCustomerRequest;
            delete request.portfolioId; // DrivewealthEnrollment does not have this field;
            if (!request.managedRtqScore) {
                const rtqStore = useRiskToleranceQuestionnaireStore();
                request.managedRtqScore = rtqStore.getRtqScore;
            }
            const payload = { ...request, isPreenrolled: true } as DrivewealthEnrollmentDto;

            this.setEnrollmentLoading({});

            return drivewealthService.postPreenrollment(payload)
                .then(result => {
                    this.setEnrollmentValueLoaded({
                        data: result.data,
                        fetchKey: {}
                    });
                    const notifierStore = useNotifierStore();
                    notifierStore.setSuccess("Enrollment in investment account successful.");
                })
                .catch(err => {
                    this.setPreenrollmentUnitialized();
                    return storeHelper.handleError({ error: err, rethrow: true });
                });
        },

        createCustomerAccount (): Promise<any> {
            const notifierStore = useNotifierStore();
            const drivewealthStore = useDrivewealthStore();
            const payload: DrivewealthCreateCustomerRequest = this.createCustomerRequest;

            if (payload.accountType === "RIA_MANAGED" && !payload.portfolioId) {
                const notifierStore = useNotifierStore();
                notifierStore.setDanger(
                    "Could not find investment portfolio, please take risk assessment quiz and accept a portfolio."
                );
                return Promise.resolve();
            }

            this.setCreateAccountFetchState(FetchState.Loading);
            return drivewealthService.createCustomerAccount(payload)
                .then(result => {
                    const drivewealthAccountManagementType = result.data.investmentAccountType;
                    const accountTypeDisplayCopy = mapDrivewealthAccountManagementTypeToDisplayCopy(drivewealthAccountManagementType);
                    notifierStore.setSuccess(`${accountTypeDisplayCopy} option created.`);
                    this.setCreateAccountFetchState(FetchState.Loaded);
                })
                .then(_ => {
                    const riskStore = useRiskToleranceQuestionnaireStore();

                    // reload the account ids and account summaries
                    const drivewealthStore = useDrivewealthStore();
                    drivewealthStore.loadAccountIds({ force: true });
                    drivewealthStore.loadAccountSummariesV2({ force: true });
                    riskStore.resetUserRtqEligibilityResults();

                    // we delete the sweep data on changing account type, so we need to repopulate this data
                    const drivewealthSweepStore = useDrivewealthSweepStore();
                    return drivewealthSweepStore.getSweep({ force: true });
                })
                .catch(err => {
                    this.setCreateAccountFetchState(FetchState.Uninitialized);
                    let errorType: DrivewealthCreateAccountError = "UNKNOWN";
                    if (err.response &&
                        err.response.status === 400 &&
                        err.response.data &&
                        err.response.data.errorMessage === "DW_ERROR_MISSING_PHONE_NUMBER") {
                        errorType = "MISSING_PHONE";
                    }

                    drivewealthStore.setCreateAccountError(errorType);
                    return storeHelper.handleError({ error: err, rethrow: true });
                });
        },
        claimAccount (accountId: number): Promise<any> {
            const payload: DrivewealthCreateCustomerRequest = this.createCustomerRequest;
            const drivewealthStore = useDrivewealthStore();
            const notifierStore = useNotifierStore();
            const drivewealthSweepStore = useDrivewealthSweepStore();

            drivewealthStore.setAccountClaimsFetchState(FetchState.Loading);
            return drivewealthService.claimAccount(accountId, payload)
                .then(result => {
                    const accountClaim = result.data;
                    drivewealthStore.updateAccountClaim(accountClaim);
                    notifierStore.setSuccess("Investment account claims saved.");
                    drivewealthStore.setAccountClaimsFetchState(FetchState.Loaded);
                })
                .then(_ => {
                    // we delete the sweep data on changing account type, so we need to repopulate this data
                    return drivewealthSweepStore.getSweep({ force: true });
                })
                .catch(err => {
                    drivewealthStore.setAccountClaimsFetchState(FetchState.Uninitialized);
                    let errorType: DrivewealthCreateAccountError = "UNKNOWN";
                    if (err.response &&
                        err.response.status === 400 &&
                        err.response.data &&
                        err.response.data.errorMessage === "DW_ERROR_MISSING_PHONE_NUMBER") {
                        errorType = "MISSING_PHONE";
                    }

                    drivewealthStore.setCreateAccountError(errorType);
                    if (errorType === "UNKNOWN") {
                        return storeHelper.handleError({ error: err, rethrow: true });
                    } else {
                        return Promise.reject();
                    }
                });
        },

        loadAllowedInvestmentAccountTypes (
            options: {
                username?: string,
                partnerId?: number,
                force?: boolean
            }): Promise<any> {

            if (options && options.force) {
                useEnrollmentTypeSubStore().resetState();
            }
            return useEnrollmentTypeSubStore().fetchKeyedApiCall(options)
                .catch(err => storeHelper.handleError({ error: err, rethrow: true }));
        },
        clearEnrollmentData (options: {username?: string, partnerId?: number}) {
            const partialFetchKey = options || {};
            const fetchKey: FetchStateKey = {
                username: partialFetchKey.username,
                partnerId: partialFetchKey.partnerId
            };

            this.invalidateEnrollmentLoadedState(fetchKey);

            this.setEnrollmentValueLoaded({
                data: emptyEnrollment(),
                fetchKey: options
            });
        },
        loadExistingPreenrollment (
            options: {
                username?: string,
                partnerId?: number
            }): Promise<any> {

            const partialFetchKey = options || {};
            const fetchKey: FetchStateKey = {
                username: partialFetchKey.username,
                partnerId: partialFetchKey.partnerId
            };

            this.invalidateEnrollmentLoadedState(fetchKey);

            if (this.enrollment.fetchState !== FetchState.Uninitialized) {
                return Promise.resolve(null);
            }

            this.setEnrollmentLoading(fetchKey);

            return drivewealthService.getEnrollmentStatus(fetchKey.username, fetchKey.partnerId).then(response => {
                this.setEnrollmentValueLoaded({ fetchKey, data: response.data });
                return response;
            }).catch(err => {
                if (errorIsStatus(err, 404)) {
                    this.setEnrollmentValueLoaded({
                        data: emptyEnrollment(),
                        fetchKey: options
                    });
                    return Promise.resolve();
                }
                return storeHelper.handleError({ error: err, rethrow: true });
            });
        },
        deleteExistingPreenrollment () {
            if (this.enrollment.fetchState === FetchState.Loading) {
                return;
            }

            const signingDocumentStore = useSigningDocumentStore();

            this.setPreenrollmentFetchState(FetchState.Loading);
            return drivewealthService.deletePreenrollment()
                .then(resp => {
                    signingDocumentStore.markDeleteSignature(signingDocumentStore.signedKey(this.enrollmentAccountType));
                    this.setPreenrollmentFetchState(FetchState.Uninitialized);
                    this.resetPreenrollment();
                })
                .catch(error => storeHelper.handleError({ error, rethrow: true }));
        },
        loadBalanceAndIsAllowedToPreenroll (options: {
            username?: string,
            partnerId?: number
        }) {
            const investmentDailyBalanceStore = useInvestmentDailyBalanceStore();
            return Promise.all([
                this.loadAllowedInvestmentAccountTypes(options),
                investmentDailyBalanceStore.loadHsaBalanceAndThreshold(options) ]
            ).catch(err => storeHelper.handleError({ error: err, rethrow: true }));
        }
    }
});